import React, { useMemo, useState } from "react";
import { DataTable } from "lift-ui";
import type { TableColumn } from "lift-ui";
import { useClaudeCode } from "../../context/ClaudeCodeContext";
import type { LiteLLMKeyRow } from "../../utils/types";

// Format spend as USD with up to 4 decimal places so micro-spends ($0.0023)
// don't round to zero in the table.
const usd = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const num = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
};

// Budget resets land on UTC midnight ("2026-09-01T00:00:00Z"). Formatting in
// local time would show the previous day for anyone west of UTC, so pin to UTC.
const resetLabel = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

type KeyRowWithSr = LiteLLMKeyRow & { _srNo: number };

const KeyUsage: React.FC = () => {
  const {
    keyUsage,
    keyUsageLoading,
    keyUsageTotal,
    keyUsageFilters,
    setKeyUsageFilters,
  } = useClaudeCode();

  const [sortKey, setSortKey] = useState<string | null>("range_spend");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const totalPages = Math.max(
    1,
    Math.ceil(keyUsageTotal / keyUsageFilters.page_size)
  );

  const accessors = useMemo<
    Record<string, (k: LiteLLMKeyRow) => string | number | null>
  >(
    () => ({
      key_alias: (k) => (k.key_alias || "").toLowerCase(),
      team_id: (k) => (k.team_alias || k.team_id || "").toLowerCase(),
      max_budget: (k) => k.max_budget ?? -1,
      range_spend: (k) => k.range_spend,
      cycle_spend: (k) => k.cycle_spend,
      lifetime_spend: (k) => k.lifetime_spend,
      request_count: (k) => k.request_count,
      total_tokens: (k) => k.total_tokens,
    }),
    []
  );

  const tableData = useMemo<KeyRowWithSr[]>(() => {
    const sorted = sortKey
      ? [...keyUsage].sort((a, b) => {
          const accessor = accessors[sortKey];
          if (!accessor) return 0;
          const aVal = accessor(a);
          const bVal = accessor(b);
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          if (typeof aVal === "number" && typeof bVal === "number") {
            return sortDir === "desc" ? bVal - aVal : aVal - bVal;
          }
          const cmp = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
          });
          return sortDir === "desc" ? -cmp : cmp;
        })
      : keyUsage;
    return sorted.map((k, i) => ({
      ...k,
      _srNo: keyUsageFilters.page * keyUsageFilters.page_size + i + 1,
    }));
  }, [
    keyUsage,
    sortKey,
    sortDir,
    accessors,
    keyUsageFilters.page,
    keyUsageFilters.page_size,
  ]);

  const columns = useMemo(
    (): TableColumn<KeyRowWithSr>[] => [
      {
        key: "_srNo",
        header: "Sr No",
        accessor: (row) => row._srNo,
        width: "70px",
        render: (_, row) => (
          <span className="text-gray-600">{row._srNo}</span>
        ),
      },
      {
        key: "key_alias",
        header: "Key",
        accessor: (row) => row.key_alias,
        sortable: true,
        width: "22%",
        render: (_, row) => (
          <div className="min-w-0">
            <div
              className="font-medium text-gray-900 truncate"
              title={row.key_alias}
            >
              {row.key_alias || "—"}
            </div>
            {row.user_id ? (
              <div
                className="text-xs text-gray-500 truncate"
                title={row.user_id}
              >
                {row.user_id}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "team_id",
        header: "Team",
        accessor: (row) => row.team_alias || row.team_id || "",
        sortable: true,
        width: "14%",
        render: (_, row) => (
          <span className="text-gray-700 truncate" title={row.team_id ?? ""}>
            {row.team_alias || row.team_id || "—"}
          </span>
        ),
      },
      {
        key: "models",
        header: "Models",
        accessor: () => "",
        width: "22%",
        render: (_, row) => {
          const allowed = row.models || [];
          const used = row.models_used_in_range || [];
          // Prefer "models actually called in this range"; fall back to the
          // key's allowed list when nothing was called yet.
          const display = used.length > 0 ? used : allowed;
          if (display.length === 0) {
            return <span className="text-gray-400 text-sm">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {display.map((m) => (
                <span
                  key={m}
                  className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
                  title={m}
                >
                  {m}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: "max_budget",
        header: "Budget",
        accessor: (row) => row.max_budget ?? -1,
        sortable: true,
        width: "110px",
        align: "right",
        render: (_, row) => (
          <span className="text-gray-700 text-sm tabular-nums">
            {row.max_budget == null ? "∞" : usd(row.max_budget)}
          </span>
        ),
      },
      {
        key: "cycle_spend",
        header: "Spend (cycle)",
        accessor: (row) => row.cycle_spend,
        sortable: true,
        width: "130px",
        align: "right",
        render: (_, row) => (
          <div className="flex flex-col items-end">
            <span className="text-gray-700 tabular-nums text-sm">
              {usd(row.cycle_spend)}
            </span>
            <span className="text-xs text-gray-400">
              {row.budget_reset_at
                ? `resets ${resetLabel(row.budget_reset_at)}`
                : "no cycle"}
            </span>
          </div>
        ),
      },
      {
        key: "range_spend",
        header: "Spend (range)",
        accessor: (row) => row.range_spend,
        sortable: true,
        width: "130px",
        align: "right",
        render: (_, row) => (
          <span className="font-semibold text-gray-900 tabular-nums">
            {usd(row.range_spend)}
          </span>
        ),
      },
      {
        key: "lifetime_spend",
        header: "Spend (lifetime)",
        accessor: (row) => row.lifetime_spend,
        sortable: true,
        width: "130px",
        align: "right",
        render: (_, row) => (
          <span
            className="text-gray-600 tabular-nums text-sm"
            title={
              row.lifetime_spend == null
                ? "Lifetime totals are temporarily unavailable"
                : undefined
            }
          >
            {usd(row.lifetime_spend)}
          </span>
        ),
      },
      {
        key: "request_count",
        header: "Requests",
        accessor: (row) => row.request_count,
        sortable: true,
        width: "100px",
        align: "right",
        render: (_, row) => (
          <span className="text-gray-700 tabular-nums text-sm">
            {num(row.request_count)}
          </span>
        ),
      },
      {
        key: "total_tokens",
        header: "Tokens",
        accessor: (row) => row.total_tokens,
        sortable: true,
        width: "110px",
        align: "right",
        render: (_, row) => (
          <span className="text-gray-700 tabular-nums text-sm">
            {num(row.total_tokens)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <DataTable<KeyRowWithSr>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="calc(100vh - 360px)"
        columns={columns}
        data={tableData}
        loading={keyUsageLoading}
        emptyMessage="No virtual keys found in this range."
        stickyHeader
        stickyFooter
        showActions={false}
        onSort={(key, direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
        sortConfig={
          sortKey ? { key: sortKey, direction: sortDir } : undefined
        }
        pagination={{
          currentPage: keyUsageFilters.page + 1,
          totalPages,
          pageSize: keyUsageFilters.page_size,
          totalItems: keyUsageTotal,
          onPageChange: (p) =>
            setKeyUsageFilters({
              ...keyUsageFilters,
              page: Math.max(0, p - 1),
            }),
          onPageSizeChange: (size) =>
            setKeyUsageFilters({
              ...keyUsageFilters,
              page_size: size,
              page: 0,
            }),
          pageSizeOptions: [25, 50, 100],
        }}
      />
    </div>
  );
};

export default KeyUsage;
